
    import Vue from "vue";
    import Component from "vue-class-component";
    import { Prop, Watch } from "vue-property-decorator";
    import { Editor, EditorContent } from '@tiptap/vue-2'
    import StarterKit from "@tiptap/starter-kit";  // https://tiptap.dev/api/extensions/starter-kit
    import TextAlign from "@tiptap/extension-text-align";
    import History from "@tiptap/extension-history";
    import { BasicDiv } from "@/tiptap/BasicDiv";
    import Link from "@tiptap/extension-link";
    import PrimaryButton from "@/vue/components/PrimaryButton.vue";
    import { IMapplicOption, MapplicOption } from "@/model/MapplicOption";


    @Component({ components: { EditorContent, PrimaryButton } })
    export default class HtmlEditor extends Vue {

        // info on v-model:
        // https://www.digitalocean.com/community/tutorials/vuejs-add-v-model-support
        // https://learnvue.co/2021/01/everything-you-need-to-know-about-vue-v-model/#using-vue-v-model-in-custom-components

        async mounted(): Promise<void> {
            this.editor = new Editor({
                content: this.value,
                extensions: [
                    StarterKit, 
                    TextAlign.configure({ types: ['heading', 'paragraph', 'basicDiv'] }), 
                    BasicDiv,
                    Link.configure({ openOnClick: false })
                ],
                onUpdate: () => {
                    const html = this.editor?.getHTML();
                    if(html != null) { 
                        this.$emit("input", html); // input event to implement v-model
                    }
                }
                // ,
                // onBlur: () => {
                //     console.log("### onBlur");
                // },
                // onFocus: () => {
                //     console.log("### onFocus");
                // }
            });

            // get focus after load
            setTimeout(() => {
                this.editor?.chain().focus().setTextSelection(0).run();
            }, 100);
        }

        beforeDestroy() {
            this.editor?.destroy();
        }

        // if you click on the editor's containing DIV, the editor can lose focus
        // and the cursor disappears - so handle the click and get focus back...
        onEditAreaClicked(event: PointerEvent) {
            // did we click directly on the div - or the editor?
            if((event?.target as HTMLElement)?.className.indexOf("editor-container") > -1) {
                this.editor?.commands.focus("end");
            }
        }

        // value prop to implement v-model
        @Prop({ default: "" }) 
        value!: string;

        @Prop({ default: 140 }) 
        height!: number;

        @Prop({ default: () => [] })
        mapplicOptionList!: Array<MapplicOption>;

        get mapplicOptions() {
            return [new MapplicOption(), ...this.mapplicOptionList];
        }

        get editorHeight() {
            if(this.height < 100) return 100;
            return this.height - 50;  // todo - cleverer way to get toolbar height?
        }

        editor: Editor|null = null;
        isEditCode: boolean = false;

        shouldShowLinkDialogue: boolean = false;
        currentLink: string = "";

        shouldShowMapplicDialogue: boolean = false;
        currentMapplicID: string = "";

        paragraphOptions =  [
            { description: "Heading 1", value: "H1" },
            { description: "Heading 2", value: "H2" },
            { description: "Heading 3", value: "H3" },
            { description: "Paragraph", value: "P" },
            { description: "Text", value: "BasicDiv" },
        ];

        get paragraphOption(): string {
            if(!this.editor) return "P";
            if(this.editor.isActive("paragraph")) return "P";
            if(this.editor.isActive("heading", { level: 1 })) return "H1";
            if(this.editor.isActive("heading", { level: 2 })) return "H2";
            if(this.editor.isActive("heading", { level: 3 })) return "H3";
            if(this.editor.isActive("basicDiv")) return "BasicDiv";
            return "P";
        }

        set paragraphOption(option: string) {
            if(!this.editor) return;
            switch(option) {
                case "H1": this.editor.chain().focus().setHeading({ level: 1 }).run(); break;
                case "H2": this.editor.chain().focus().setHeading({ level: 2 }).run(); break;
                case "H3": this.editor.chain().focus().setHeading({ level: 3 }).run(); break;
                case "P": this.editor.chain().focus().setParagraph().run(); break;
                case "BasicDiv": this.editor.chain().focus().setBasicDiv().run(); break;
                default: this.editor.chain().focus().setParagraph().run();
            }
        }

        alignmentOptions =  [
            { description: "Left", value: "left" },
            { description: "Centre", value: "center" },
            { description: "Right", value: "right" },
            { description: "Justified", value: "justified" },
        ];

        get alignmentOption(): string {
            if(!this.editor) return "left";
            if(this.editor.isActive({ textAlign: "left" })) return "left";
            if(this.editor.isActive({ textAlign: "right" })) return "right";
            if(this.editor.isActive({ textAlign: "center" })) return "center";
            if(this.editor.isActive({ textAlign: "justify" })) return "justify";
            return "left";
        }

        set alignmentOption(option: string) {
            if(!this.editor) return;
            switch(option) {
                case "left": this.editor.chain().focus().setTextAlign("left").run(); break;
                case "right": this.editor.chain().focus().setTextAlign("right").run(); break;
                case "center": this.editor.chain().focus().setTextAlign("center").run(); break;
                case "justify": this.editor.chain().focus().setTextAlign("justify").run(); break;
                default: this.editor.chain().focus().setTextAlign("left").run();
            }
        }

        get isLinkActive(): boolean {
            if(this.editor == null) return false;
            const href = this.editor.getAttributes("link").href;
            return this.editor.isActive("link") && !this.isMapplicLink(href);
        }

        get isMapplicLinkActive(): boolean {
            if(this.editor == null) return false;
            const href = this.editor.getAttributes("link").href;
            return this.editor.isActive("link") && this.isMapplicLink(href);
        }

        isMapplicLink(href: string): boolean {
            return this.mapplicOptionList.findIndex(i => i.href === href) > -1;
        }

        showLinkDialogue() {
            if(this.editor == null) return;
            this.currentLink = this.editor.getAttributes("link").href;
            this.shouldShowLinkDialogue = true;
        }

        showMapplicDialogue() {
            if(this.editor == null) return;
            const href = this.editor.getAttributes("link").href;
            // TODO - what if duplicate hrefs? use query string with ID ??? Does it matter?
            this.currentMapplicID = !href 
                ? ""
                : this.mapplicOptionList.find(i => i.href === href)?.id ?? "";
            this.shouldShowMapplicDialogue = true;
        }

        setLink() {
            if(this.editor == null) return;
            //const previousUrl = this.editor.getAttributes("link").href;
            // could maybe implement a dialogue for this?  would be async...
            //const url = window.prompt('URL', previousUrl)

            // cancelled
            //if (url === null) return;

            if (!this.currentLink) {
                // empty - remove
                this.editor.chain().focus().extendMarkRange("link").unsetLink().run();
            }
            else {
                // update
                this.editor.chain().focus().extendMarkRange("link").setLink({ href: this.currentLink }).run();
            }
            this.shouldShowLinkDialogue = false;
        }

        setMapplicLink() {
            if(this.editor == null) return;
            const option = !this.currentMapplicID ? null : this.mapplicOptionList.find(i => i.id === this.currentMapplicID);

            if (!option) {
                this.editor.chain().focus().extendMarkRange("link").unsetLink().run();
            }
            else {
                this.editor.chain().focus().extendMarkRange("link").setLink({ href: option.href }).run();
            }
            this.shouldShowMapplicDialogue = false;
        }

        unsetLink() {
            this.currentLink = "";
            this.setLink();
            //this.editor?.chain().focus().unsetLink().run();
        }

        unsetMapplicLink() {
            this.currentMapplicID = "";
            this.setMapplicLink();
        }

        @Watch("value") 
        onValuechanged(): void {
            // this must be pretty shitty and slow.but we need to make sure we don't call setContent() unless we need to...
            const currentEditorHtml = this.editor?.getHTML();
            if(this.value !== currentEditorHtml || !this.value) {
                this.editor?.commands.setContent(this.value);
            }
        }

        onTextAreaInput(html: string) {
            this.editor?.commands.setContent(html);
            this.$emit("input", html); // input event to implement v-model
        }
    }
